/**
 * A month that was paid outside the app.
 *
 * Owner, Sep 28 2026: "mark all as paid the invoice for August — they were
 * all paid through Jobber, make sure no duplicates." Look first: every open
 * bill for the month, one per customer, with an exact double called out.
 * Then one press marks them paid (one payment row each, source written down)
 * and voids the doubles.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { businessToday } from '../utils/time';

const money = (n: any) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const lastMonth = () => { const t = businessToday(); const d = new Date(`${t.slice(0, 7)}-01T12:00:00`); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

export default function PaidOutsideCard() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(lastMonth());
  // "All sent bills" — every bill that went out and was never marked paid, any month (collected in Jobber).
  const [allSent, setAllSent] = useState(false);
  const scope = allSent ? 'all' : month;
  const [source, setSource] = useState('jobber');
  const [look, setLook] = useState<any>(null);

  const run = useMutation((dryRun: boolean) => (dryRun
    ? api.get('/billing/paid-outside', { params: { month: scope, source } })
    : api.post('/billing/paid-outside', { month: scope, source })).then(r => r.data), {
    onSuccess: (d: any) => {
      setLook(d);
      if (!d.dry_run) {
        toast.success(d?.message || 'Done', { autoClose: 9000 });
        qc.invalidateQueries('billing-dashboard'); qc.invalidateQueries('invoices'); qc.invalidateQueries('money-today'); qc.invalidateQueries('money-month');
      }
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not do that'); },
  });

  const monthName = allSent ? 'every month (sent bills only)' : new Date(`${month}-02T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="card p-4 space-y-2">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full text-left flex items-center justify-between gap-2">
        <span className="text-sm font-bold text-gray-900">Bills paid outside the app (Jobber)</span>
        <span className="text-xs text-gray-500">{open ? 'Hide' : 'Open'}</span>
      </button>
      {open && (
        <>
          <p className="text-xs text-gray-600">{allSent ? 'Every bill that was sent and never marked paid — any month, drafts left alone — gets marked paid on its period’s last day' : 'Every open bill for the month gets marked paid'} — once each, with the source written on the payment. An exact double (same customer, same period, same total) is voided, never paid twice.</p>
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <label className="flex items-center gap-1.5 text-xs text-gray-700">
              <input type="checkbox" checked={allSent} onChange={e => { setAllSent(e.target.checked); setLook(null); }} />
              All sent bills, any month
            </label>
            {!allSent && <input type="month" value={month} onChange={e => { setMonth(e.target.value); setLook(null); }} className="input py-1.5" />}
            <select value={source} onChange={e => { setSource(e.target.value); setLook(null); }} className="input py-1.5">
              {[['jobber', 'Jobber'], ['cash', 'Cash'], ['check', 'Check'], ['zelle', 'Zelle'], ['venmo', 'Venmo'], ['other', 'Other']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="button" onClick={() => run.mutate(true)} disabled={run.isLoading}
              className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-800 text-white font-semibold disabled:opacity-50">
              {run.isLoading ? 'Working…' : 'Look first'}
            </button>
          </div>
          {look && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-2.5 text-xs text-gray-800 space-y-1 max-h-72 overflow-auto">
              <p className="font-semibold">
                {look.dry_run
                  ? `${look.count} ${allSent ? 'sent' : 'open'} bill${look.count === 1 ? '' : 's'} for ${monthName} · ${money(look.total)}${look.already_paid?.count ? ` · ${look.already_paid.count} already paid` : ''}`
                  : look.message}
              </p>
              {(look.keep || []).map((r: any) => (
                <p key={r.id}>{allSent && r.paid_on ? <span className="text-gray-500">{r.paid_on} · </span> : null}{r.customer_name} — {r.invoice_number} · {money(r.balance)}{Number(r.paid_so_far) > 0 ? ` (of ${money(r.total)}, ${money(r.paid_so_far)} already in)` : ''} · {r.status}</p>
              ))}
              {(look.dupes || []).map((d: any) => (
                <p key={d.id} className="text-amber-800"><b>Duplicate:</b> {d.customer_name} — {d.invoice_number} · {money(d.total)} is a double of {d.duplicate_of} — {look.dry_run ? 'will be voided' : 'voided'}</p>
              ))}
              {!(look.keep || []).length && !(look.dupes || []).length && <p className="text-gray-500">Nothing open for {monthName}.</p>}
            </div>
          )}
          {look?.dry_run && (look.count > 0 || (look.dupes || []).length > 0) && (
            <button type="button" onClick={() => { if (window.confirm(`Mark ${look.count} bill${look.count === 1 ? '' : 's'} for ${monthName} paid through ${source}${(look.dupes || []).length ? ` and void ${look.dupes.length} duplicate${look.dupes.length === 1 ? '' : 's'}` : ''}?`)) run.mutate(false); }}
              disabled={run.isLoading} className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
              Mark {look.count} paid through {source.charAt(0).toUpperCase() + source.slice(1)}{(look.dupes || []).length ? ` · void ${look.dupes.length} duplicate${look.dupes.length === 1 ? '' : 's'}` : ''}
            </button>
          )}
          <p className="text-[11px] text-gray-500">Nothing is texted or charged.</p>
        </>
      )}
    </div>
  );
}
